import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { UserPlus, Mail, Lock, Key, AlertCircle, CheckCircle, Home } from 'lucide-react'
import { registerUser } from '../services/auth'
import { validateAccessKey } from '../services/accessKeys'
import { createOrUpdateTutorProfile } from '../services/firestore'
import './Register.css'

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    accessKey: '',
    tutorType: 'standard',
    tutorRoles: ['vtic']
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [keyValidated, setKeyValidated] = useState(false)
  const [detectedRole, setDetectedRole] = useState(null)
  const [detectedLabel, setDetectedLabel] = useState('')
  const navigate = useNavigate()

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
    setError('')
  }

  const validateKey = () => {
    const result = validateAccessKey(formData.accessKey)
    if (result.success) {
      setKeyValidated(true)
      setDetectedRole(result.role)
      setDetectedLabel(result.label)
      setError('')
    } else {
      setKeyValidated(false)
      setDetectedRole(null)
      setDetectedLabel('')
      setError(result.error)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // Validaciones
    if (!keyValidated) {
      setError('Por favor, valida tu llave de acceso primero')
      return
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden')
      return
    }

    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres')
      return
    }

    setLoading(true)

    try {
      // Registrar usuario en Firebase Auth
      const authResult = await registerUser(
        formData.email,
        formData.password,
        formData.fullName
      )

      if (!authResult.success) {
        setError(authResult.error || 'Error al crear la cuenta')
        setLoading(false)
        return
      }

      // Crear perfil en Firestore
      const isRegisteredAdmin = detectedRole === 'admin' || formData.email.toLowerCase() === 'admin@vtic.com'
      const assignedRoles = detectedRole === 'admin' ? ['vtic'] : [detectedRole]
      const tutorType = detectedRole === 'eucs' ? 'eucs' : 'standard'

      const profileResult = await createOrUpdateTutorProfile(authResult.user.uid, {
        email: formData.email,
        fullName: formData.fullName,
        tutorType: tutorType,
        tutorRoles: assignedRoles,
        isAdmin: isRegisteredAdmin,
        bio: '',
        career: '',
        year: '',
        phone: '',
        location: '',
        skills: [],
        achievements: [],
        avatar: ''
      })

      if (profileResult.success) {
        navigate('/dashboard')
      } else {
        setError('Error al crear el perfil. Por favor, intenta nuevamente.')
      }
    } catch (err) {
      setError(err.message || 'Error al crear la cuenta')
    }

    setLoading(false)
  }

  return (
    <div className="register-page">
      <div className="register-container">
        <button 
          className="home-link-button"
          onClick={() => navigate('/')}
        >
          <Home size={18} />
          Volver al Inicio
        </button>
        <div className="register-header">
          <h1>Registro de Tutor</h1>
          <p>Únete al equipo de tutores de VTIC</p>
        </div>

        <form onSubmit={handleSubmit} className="register-form">
          <div className="form-group">
            <label htmlFor="accessKey">
              <Key size={18} />
              Llave de Acceso *
            </label>
            <div className="access-key-group">
              <input
                type="text"
                id="accessKey"
                name="accessKey"
                value={formData.accessKey}
                onChange={handleChange}
                onBlur={validateKey}
                placeholder="Ingresa la llave de acceso"
                required
                disabled={loading || keyValidated}
                className={keyValidated ? 'validated' : ''}
              />
              {keyValidated && (
                <CheckCircle size={20} className="check-icon" />
              )}
            </div>
            {keyValidated && (
              <p className="validation-message success">
                Llave de acceso válida — Rol asignado: <strong>{detectedLabel}</strong>
              </p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="fullName">
              <UserPlus size={18} />
              Nombre Completo *
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="Tu nombre completo"
              required
              disabled={loading || !keyValidated}
              autoComplete="name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">
              <Mail size={18} />
              Correo Electrónico *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="tu@unah.hn"
              required
              disabled={loading || !keyValidated}
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">
              <Lock size={18} />
              Contraseña *
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Mínimo 6 caracteres"
              required
              disabled={loading || !keyValidated}
              minLength={6}
              autoComplete="new-password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">
              <Lock size={18} />
              Confirmar Contraseña *
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Repite tu contraseña"
              required
              disabled={loading || !keyValidated}
              minLength={6}
              autoComplete="new-password"
            />
          </div>

          {error && (
            <div className="error-message">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          <button 
            type="submit" 
            className="register-button"
            disabled={loading || !keyValidated}
          >
            <UserPlus size={20} />
            {loading ? 'Creando cuenta...' : 'Registrarse'}
          </button>
        </form>

        <div className="register-footer">
          <p>¿Ya tienes una cuenta?</p>
          <Link to="/login" className="login-link">
            Inicia sesión aquí
          </Link>
        </div>
      </div>
    </div>
  )
}

export default Register
